import { useState, useEffect } from 'react';
import {
  Zap,
  X,
  ShieldCheck,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
  CheckCircle2
} from 'lucide-react';

/**
 * SimplifyDebtsExplainerModal
 *
 * Interactive visual modal that illustrates how debt simplification works
 * using a 3-person scenario (Ana, Bob, Charlie) with before/after comparison,
 * net balance mathematical conservation proof, and interactive FAQs.
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Callback when modal is dismissed
 */
export default function SimplifyDebtsExplainerModal({
  isOpen = false,
  onClose = () => {}
}) {
  const [activeStage, setActiveStage] = useState('after');
  const [openFaq, setOpenFaq] = useState(0);

  // Close on Escape key press
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const faqs = [
    {
      q: 'Will I ever pay more than my share?',
      a: 'Never. The simplification algorithm is mathematically proven to strictly conserve net balances. If you owe 10 DH net across all group expenses, you will never pay more than 10 DH total. Creditors receive their exact balance down to the cent.'
    },
    {
      q: "Why am I paying someone I didn't borrow from?",
      a: 'Bob was an intermediary middle-man who was owed 10 DH by Ana and owed 10 DH to Charlie. By having Ana pay Charlie directly, Bob is settled automatically with zero transfers needed, saving the group unnecessary transactions.'
    },
    {
      q: 'What about multiple currencies?',
      a: 'Tezer partitions group expenses by currency (e.g. AED, USD, EUR) and resolves independent debt graphs for each currency. There is never cross-contamination or arbitrary exchange rate conversion.'
    }
  ];

  return (
    <div
      className="modal-backdrop modal-backdrop-blur animate-fade-in"
      onClick={onClose}
      role="presentation"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}
    >
      <div
        className="glass-panel modal-spring-enter modal-content-panel"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="simplify-debts-modal-title"
        style={{
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: 'var(--bg-secondary, #121824)',
          borderRadius: '24px',
          padding: '24px',
          border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.08))',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          boxSizing: 'border-box',
          color: 'var(--text-main, #ffffff)'
        }}
      >
        {/* 1. Modal Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '12px',
                background: 'rgba(0, 245, 212, 0.15)',
                border: '1px solid rgba(0, 245, 212, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--accent-cyan, #00f5d4)',
                flexShrink: 0
              }}
            >
              <Zap size={22} />
            </div>
            <div>
              <h2
                id="simplify-debts-modal-title"
                style={{
                  margin: 0,
                  fontSize: '18px',
                  fontWeight: '700',
                  color: 'var(--text-main, #ffffff)',
                  lineHeight: '1.2'
                }}
              >
                How Simplify Debts Works
              </h2>
              <p
                style={{
                  margin: '4px 0 0',
                  fontSize: '12px',
                  color: 'var(--text-muted, #94a3b8)',
                  lineHeight: '1.4'
                }}
              >
                Restructures group debt without changing anyone's net balance
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            style={{
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.08))',
              borderRadius: '10px',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--text-muted, #94a3b8)',
              cursor: 'pointer',
              flexShrink: 0,
              transition: 'background 0.15s ease'
            }}
          >
            <X size={18} />
          </button>
        </div>

        {/* 2. Interactive Stage Switcher Pill */}
        <div
          style={{
            display: 'flex',
            background: 'rgba(0, 0, 0, 0.35)',
            padding: '4px',
            borderRadius: '14px',
            border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.08))'
          }}
        >
          <button
            type="button"
            onClick={() => setActiveStage('before')}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '10px',
              border: activeStage === 'before' ? '1px solid rgba(255, 107, 107, 0.4)' : '1px solid transparent',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: activeStage === 'before' ? 'rgba(255, 82, 82, 0.18)' : 'transparent',
              color: activeStage === 'before' ? '#ff6b6b' : 'var(--text-muted, #94a3b8)'
            }}
          >
            Before: 2 Payments
          </button>
          <button
            type="button"
            onClick={() => setActiveStage('after')}
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '10px',
              border: activeStage === 'after' ? '1px solid rgba(0, 245, 212, 0.4)' : '1px solid transparent',
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: activeStage === 'after' ? 'rgba(0, 245, 212, 0.18)' : 'transparent',
              color: activeStage === 'after' ? 'var(--accent-cyan, #00f5d4)' : 'var(--text-muted, #94a3b8)',
              boxShadow: activeStage === 'after' ? '0 2px 8px rgba(0, 245, 212, 0.15)' : 'none'
            }}
          >
            After: 1 Payment (Simplified)
          </button>
        </div>

        {/* 2. Visual SVG/CSS Diagram Area */}
        <div
          style={{
            background: 'rgba(0, 0, 0, 0.28)',
            border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.06))',
            borderRadius: '18px',
            padding: '16px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          <div style={{ width: '100%', position: 'relative' }}>
            <svg
              viewBox="0 0 440 170"
              style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}
            >
              <defs>
                <marker
                  id="arrow-red"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1.5 L 9 5 L 0 8.5 z" fill="#ff6b6b" />
                </marker>
                <marker
                  id="arrow-cyan"
                  viewBox="0 0 10 10"
                  refX="8"
                  refY="5"
                  markerWidth="6"
                  markerHeight="6"
                  orient="auto-start-reverse"
                >
                  <path d="M 0 1.5 L 9 5 L 0 8.5 z" fill="#00f5d4" />
                </marker>
                <linearGradient id="ana-avatar" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#ff7a18" />
                  <stop offset="100%" stopColor="#ff5238" />
                </linearGradient>
                <linearGradient id="bob-avatar" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#475569" />
                  <stop offset="100%" stopColor="#334155" />
                </linearGradient>
                <linearGradient id="charlie-avatar" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#00b4d8" />
                  <stop offset="100%" stopColor="#0077b6" />
                </linearGradient>
              </defs>

              {/* === BEFORE STATE ARROWS === */}
              {activeStage === 'before' && (
                <g className="animate-fade-in">
                  {/* Ana -> Bob arrow (Owes 10 DH) */}
                  <line
                    x1="88"
                    y1="90"
                    x2="190"
                    y2="90"
                    stroke="#ff6b6b"
                    strokeWidth="2.5"
                    markerEnd="url(#arrow-red)"
                  />
                  <rect
                    x="110"
                    y="64"
                    width="60"
                    height="20"
                    rx="10"
                    fill="#2a1215"
                    stroke="#ff6b6b"
                    strokeWidth="1"
                  />
                  <text
                    x="140"
                    y="78"
                    textAnchor="middle"
                    fill="#ff8a80"
                    fontSize="10"
                    fontWeight="700"
                  >
                    10 DH
                  </text>

                  {/* Bob -> Charlie arrow (Owes 10 DH) */}
                  <line
                    x1="248"
                    y1="90"
                    x2="350"
                    y2="90"
                    stroke="#ff6b6b"
                    strokeWidth="2.5"
                    markerEnd="url(#arrow-red)"
                  />
                  <rect
                    x="270"
                    y="64"
                    width="60"
                    height="20"
                    rx="10"
                    fill="#2a1215"
                    stroke="#ff6b6b"
                    strokeWidth="1"
                  />
                  <text
                    x="300"
                    y="78"
                    textAnchor="middle"
                    fill="#ff8a80"
                    fontSize="10"
                    fontWeight="700"
                  >
                    10 DH
                  </text>
                </g>
              )}

              {/* === AFTER STATE ARROWS === */}
              {activeStage === 'after' && (
                <g className="animate-fade-in">
                  {/* Bypassed dashed routes */}
                  <line
                    x1="88"
                    y1="90"
                    x2="190"
                    y2="90"
                    stroke="#475569"
                    strokeWidth="1.5"
                    strokeDasharray="4 4"
                    opacity="0.3"
                  />
                  <line
                    x1="248"
                    y1="90"
                    x2="350"
                    y2="90"
                    stroke="#475569"
                    strokeWidth="1.5"
                    strokeDasharray="4 4"
                    opacity="0.3"
                  />

                  {/* Direct Curved Arrow: Ana -> Charlie */}
                  <path
                    d="M 68 68 C 120 10, 320 10, 372 68"
                    fill="none"
                    stroke="#00f5d4"
                    strokeWidth="2.8"
                    markerEnd="url(#arrow-cyan)"
                  />
                  {/* Direct Badge Pill */}
                  <rect
                    x="165"
                    y="14"
                    width="110"
                    height="22"
                    rx="11"
                    fill="#082b27"
                    stroke="#00f5d4"
                    strokeWidth="1.2"
                  />
                  <text
                    x="220"
                    y="29"
                    textAnchor="middle"
                    fill="#00f5d4"
                    fontSize="11"
                    fontWeight="bold"
                  >
                    10 DH Direct
                  </text>
                </g>
              )}

              {/* === NODE 1: ANA === */}
              <g>
                <circle
                  cx="60"
                  cy="90"
                  r="24"
                  fill="url(#ana-avatar)"
                  stroke="#ff7a18"
                  strokeWidth="2"
                />
                <text
                  x="60"
                  y="96"
                  textAnchor="middle"
                  fill="#ffffff"
                  fontSize="14"
                  fontWeight="bold"
                >
                  A
                </text>
                <text
                  x="60"
                  y="128"
                  textAnchor="middle"
                  fill="var(--text-main, #ffffff)"
                  fontSize="12"
                  fontWeight="600"
                >
                  Ana
                </text>
                <text
                  x="60"
                  y="144"
                  textAnchor="middle"
                  fill="#ff6b6b"
                  fontSize="10"
                  fontWeight="500"
                >
                  Owes 10 DH
                </text>
              </g>

              {/* === NODE 2: BOB === */}
              <g>
                {activeStage === 'after' ? (
                  <>
                    <circle
                      cx="220"
                      cy="90"
                      r="24"
                      fill="rgba(16, 185, 129, 0.15)"
                      stroke="#10b981"
                      strokeWidth="2"
                    />
                    <text
                      x="220"
                      y="96"
                      textAnchor="middle"
                      fill="#10b981"
                      fontSize="14"
                      fontWeight="bold"
                    >
                      B
                    </text>
                    {/* Settled Green Check Badge */}
                    <circle cx="238" cy="72" r="9" fill="#10b981" />
                    <path
                      d="M 234 72 L 237 75 L 243 69"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <text
                      x="220"
                      y="128"
                      textAnchor="middle"
                      fill="#10b981"
                      fontSize="12"
                      fontWeight="600"
                    >
                      Bob
                    </text>
                    <text
                      x="220"
                      y="144"
                      textAnchor="middle"
                      fill="#10b981"
                      fontSize="10"
                      fontWeight="700"
                    >
                      Settled ($0.00)
                    </text>
                  </>
                ) : (
                  <>
                    <circle
                      cx="220"
                      cy="90"
                      r="24"
                      fill="url(#bob-avatar)"
                      stroke="#64748b"
                      strokeWidth="2"
                    />
                    <text
                      x="220"
                      y="96"
                      textAnchor="middle"
                      fill="#ffffff"
                      fontSize="14"
                      fontWeight="bold"
                    >
                      B
                    </text>
                    <text
                      x="220"
                      y="128"
                      textAnchor="middle"
                      fill="var(--text-main, #ffffff)"
                      fontSize="12"
                      fontWeight="600"
                    >
                      Bob
                    </text>
                    <text
                      x="220"
                      y="144"
                      textAnchor="middle"
                      fill="#94a3b8"
                      fontSize="10"
                      fontWeight="500"
                    >
                      Middle-man ($0)
                    </text>
                  </>
                )}
              </g>

              {/* === NODE 3: CHARLIE === */}
              <g>
                <circle
                  cx="380"
                  cy="90"
                  r="24"
                  fill="url(#charlie-avatar)"
                  stroke="#00b4d8"
                  strokeWidth="2"
                />
                <text
                  x="380"
                  y="96"
                  textAnchor="middle"
                  fill="#ffffff"
                  fontSize="14"
                  fontWeight="bold"
                >
                  C
                </text>
                <text
                  x="380"
                  y="128"
                  textAnchor="middle"
                  fill="var(--text-main, #ffffff)"
                  fontSize="12"
                  fontWeight="600"
                >
                  Charlie
                </text>
                <text
                  x="380"
                  y="144"
                  textAnchor="middle"
                  fill="#10b981"
                  fontSize="10"
                  fontWeight="500"
                >
                  Gets 10 DH
                </text>
              </g>
            </svg>
          </div>

          {/* Diagram Status Badge */}
          {activeStage === 'before' ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '9px 14px',
                borderRadius: '12px',
                background: 'rgba(255, 107, 107, 0.12)',
                border: '1px solid rgba(255, 107, 107, 0.25)',
                color: '#ff8a80',
                fontSize: '12px',
                fontWeight: '600',
                textAlign: 'center'
              }}
            >
              <Clock size={15} style={{ flexShrink: 0 }} />
              <span>2 payments required to settle group</span>
            </div>
          ) : (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '9px 14px',
                borderRadius: '12px',
                background: 'rgba(0, 245, 212, 0.12)',
                border: '1px solid rgba(0, 245, 212, 0.3)',
                color: 'var(--accent-cyan, #00f5d4)',
                fontSize: '12px',
                fontWeight: '600',
                textAlign: 'center'
              }}
            >
              <Sparkles size={15} style={{ flexShrink: 0 }} />
              <span>1 payment settles the whole group! (Saved 50% transactions)</span>
            </div>
          )}
        </div>

        {/* 3. Mathematical Conservation Guarantee Card */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.03)',
            border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.08))',
            borderRadius: '16px',
            padding: '16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <ShieldCheck size={18} color="var(--accent-cyan, #00f5d4)" />
            <span style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-main, #ffffff)' }}>
              Mathematical Conservation Guarantee
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Ana */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '10px',
                fontSize: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '600', color: 'var(--text-main, #ffffff)' }}>Ana</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#ff6b6b', fontWeight: '700' }}>Net -10 DH</span>
                <span
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '6px',
                    background: 'rgba(0, 245, 212, 0.12)',
                    color: 'var(--accent-cyan, #00f5d4)',
                    fontWeight: '600'
                  }}
                >
                  Unchanged
                </span>
              </div>
            </div>

            {/* Bob */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '10px',
                fontSize: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '600', color: 'var(--text-main, #ffffff)' }}>Bob</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#94a3b8', fontWeight: '700' }}>Net $0.00</span>
                <span
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '6px',
                    background: 'rgba(0, 245, 212, 0.12)',
                    color: 'var(--accent-cyan, #00f5d4)',
                    fontWeight: '600'
                  }}
                >
                  Unchanged
                </span>
              </div>
            </div>

            {/* Charlie */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: 'rgba(255, 255, 255, 0.02)',
                borderRadius: '10px',
                fontSize: '12px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: '600', color: 'var(--text-main, #ffffff)' }}>Charlie</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#10b981', fontWeight: '700' }}>Net +10 DH</span>
                <span
                  style={{
                    fontSize: '10px',
                    padding: '2px 6px',
                    borderRadius: '6px',
                    background: 'rgba(0, 245, 212, 0.12)',
                    color: 'var(--accent-cyan, #00f5d4)',
                    fontWeight: '600'
                  }}
                >
                  Unchanged
                </span>
              </div>
            </div>
          </div>

          <div
            style={{
              paddingTop: '8px',
              borderTop: '1px solid rgba(255, 255, 255, 0.06)',
              fontSize: '11px',
              color: 'var(--text-muted, #94a3b8)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              lineHeight: '1.4'
            }}
          >
            <CheckCircle2 size={14} color="var(--accent-cyan, #00f5d4)" style={{ flexShrink: 0 }} />
            <span>Total debt is conserved down to the cent. No member ever pays more or receives less than their true share.</span>
          </div>
        </div>

        {/* 4. FAQ Section */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <h3
            style={{
              margin: '0 0 4px',
              fontSize: '13px',
              fontWeight: '700',
              color: 'var(--text-muted, #94a3b8)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Frequently Asked Questions
          </h3>

          {faqs.map((faq, index) => {
            const isExpanded = openFaq === index;
            return (
              <div
                key={faq.q}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-glass, rgba(255, 255, 255, 0.06))',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(isExpanded ? null : index)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    background: 'transparent',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    cursor: 'pointer',
                    color: 'var(--text-main, #ffffff)',
                    fontSize: '13px',
                    fontWeight: '600',
                    textAlign: 'left'
                  }}
                >
                  <span>{faq.q}</span>
                  {isExpanded ? (
                    <ChevronUp size={16} color="var(--accent-cyan, #00f5d4)" style={{ flexShrink: 0 }} />
                  ) : (
                    <ChevronDown size={16} color="var(--text-muted, #94a3b8)" style={{ flexShrink: 0 }} />
                  )}
                </button>

                {isExpanded && (
                  <div
                    style={{
                      padding: '0 14px 12px',
                      fontSize: '12px',
                      color: 'var(--text-muted, #94a3b8)',
                      lineHeight: '1.5',
                      borderTop: '1px solid rgba(255, 255, 255, 0.04)',
                      paddingTop: '8px'
                    }}
                  >
                    {faq.a}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* 5. Got it Action Button */}
        <button
          type="button"
          onClick={onClose}
          style={{
            width: '100%',
            padding: '12px',
            borderRadius: '14px',
            border: 'none',
            background: 'linear-gradient(135deg, #00f5d4 0%, #00b4d8 100%)',
            color: '#08101a',
            fontSize: '14px',
            fontWeight: '700',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0, 245, 212, 0.3)',
            transition: 'opacity 0.15s ease',
            marginTop: '4px'
          }}
        >
          Got it
        </button>
      </div>
    </div>
  );
}
